import React from 'react';
import { FormTheme } from '../types/formTheme';

interface FormInputProps {
    id: string;
    type?: 'text' | 'email' | 'tel' | 'number' | 'date' | 'url';
    value: string;
    onChange: (value: string) => void;
    onBlur?: () => void;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
    error?: string;
    maxLength?: number;
    pattern?: string;
    autoComplete?: string;
    appearance?: FormTheme;
    className?: string;
}

const FormInput: React.FC<FormInputProps> = ({
    id,
    type = 'text',
    value,
    onChange,
    onBlur,
    placeholder,
    required = false,
    disabled = false,
    error,
    maxLength,
    pattern,
    autoComplete,
    appearance = 'default',
    className = ''
}) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange(e.target.value);
    };

    const baseStyle = appearance === 'tracking'
        ? `
            w-full px-4 py-3 rounded-lg
            bg-white border transition-all duration-200
            text-gray-900 placeholder-gray-400
            focus:outline-none focus:ring-2 focus:ring-offset-0
            disabled:opacity-50 disabled:cursor-not-allowed
        `
        : `
            w-full px-4 py-3 rounded-xl
            bg-white/80 backdrop-blur-sm
            border-2 transition-all duration-200
            text-gray-900 placeholder-gray-400
            focus:outline-none focus:ring-2 focus:ring-offset-2
            disabled:opacity-50 disabled:cursor-not-allowed
        `;

    const stateStyle = error
        ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
        : appearance === 'tracking'
            ? 'border-gray-300 focus:border-black focus:ring-black/10 hover:border-gray-400'
            : 'border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 hover:border-gray-300';

    return (
        <input
            id={id}
            type={type}
            value={value}
            onChange={handleChange}
            onBlur={onBlur}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            maxLength={maxLength}
            pattern={pattern}
            autoComplete={autoComplete}
            className={`
                ${baseStyle}
                ${stateStyle}
                ${className}
            `}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={error ? `${id}-error` : undefined}
        />
    );
};

export default FormInput;
